import React, { useState, useEffect, useRef, useCallback } from 'react';

import css from './WallVisualizer.module.css';

// Permanent room photos (square 1254×1254 renders) from public/static/rooms/
const ROOM_BASE = '/static/rooms';

// room1 + room2 are the same fixed-camera render, so a single wall-plane
// scale applies to both. Calibrated against room1's queen headboard. The headboard is
// iso-LUMA with the wall (invisible to a brightness edge detector) but +22
// on the R−B warmth axis; measured there sub-pixel across 5 independent
// y-bands on the 1254 px native render: 563.2 px ± 1.2 px (left ≈349.6,
// right ≈913.7). Real width anchored via an independent rigid ruler — the
// two euro shams (26 in each): a king-scale headboard would force ~30 in
// shams (impossible), pinning the bed as a queen, headboard 60–66 in, with
// 64 in landing the sham block at ~50.5 in (center of the 48–52 in span two
// plumped 26 in shams present). 563.2 / 64 ≈ 8.80; we keep 8.86 (implies
// 63.6 in, within the triangulated band; residual ±~3% → a 10 in piece
// renders 9.5–10.4 in, never visibly wrong). Note this is a styled 3D
// render so the modeled bed size is the irreducible assumption — not
// recoverable from pixels; the euro-sham triangulation is the tightest
// available anchor. `anchorPct` is where the BOTTOM edge of the artwork
// sits as a % of the square scene, tuned to hover just above the furniture.
//
// room3 ("Gallery") is a DIFFERENT render (source 1402×1122, center-cropped
// to a 1254² square to match the square-stage model). It uses `centerYPct`
// instead of `anchorPct` — the artwork's CENTER is placed at the center of
// the wall (44% of the scene, the measured midpoint of the wall band
// y≈11–77%). pxPerInch is calibrated off the BASEBOARD, not the bench: the
// bench sits several feet FORWARD of the wall, so its larger perspective
// projection inflated the scale (the earlier 15.8/14 bench guesses rendered
// pieces ~3× too big). The baseboard is ON the wall plane (zero depth
// error) and a known ≈6 in tall; measured sub-pixel at the clear left +
// right margins = 32.6 px ± 0.1 px on the 1254² asset → 32.6 / 6 ≈ 5.44
// px/in. Sanity: scene = 1254/5.44 ≈ 19 ft of wall (a wide gallery shot,
// plausible); a 10 in piece renders ≈54 px (4.3% of scene). This is the
// tightest-calibrated of the three rooms (wall-plane reference, σ=0.1 px);
// the only assumption is the 6 in baseboard height (Trey-supplied).
const ROOMS = [
  {
    src: ROOM_BASE + '/room1.jpg',
    label: 'Bedroom',
    pxPerInch: 8.86,
    anchorPct: 43,
    wall: { left: 50, width: 22 },
  },
  {
    src: ROOM_BASE + '/room2.jpg',
    label: 'Living Room',
    pxPerInch: 8.86,
    anchorPct: 37.5,
    wall: { left: 50, width: 22 },
  },
  {
    src: ROOM_BASE + '/room3.jpg',
    label: 'Gallery',
    pxPerInch: 5.44, // baseboard-calibrated: 32.6px / 6in (wall-plane, see note)
    centerYPct: 44, // center the piece on the wall (not bottom-anchored)
    wall: { left: 50, width: 22 },
  },
];

// Native pixel width of the square room renders (scales pxPerInch to the stage)
const ROOM_NATIVE_WIDTH = 1254;

// Parse a listing's free-text dimensions into { w, h } in INCHES.
//
// Real catalog data is extremely messy (audited across 2378 live listings):
// curly quotes (”″), fractions ("13 1/2\""), "by", L/W/H/D labels in either
// order ("H-17" or "24 H"), a depth dimension ("24x36x1.5" — canvas /
// sculpture thickness), single-dimension "round" pieces, and inches-primary
// strings with a cm value in parens. The old strict regex left ~19% of the
// catalog unparseable (no real-world scale) and mis-converted inches→cm.
//
// This extracts the two FACE dimensions with correct magnitude + unit and
// drops depth. It does NOT try to get Height-vs-Width order right —
// orientToImage() corrects orientation from the actual image downstream, so
// the parser only needs the right two magnitudes. `framed` prefers the last
// "AxB" group when several exist (e.g. image size vs framed/sheet size).
function parseDimensions(str, framed) {
  if (!str) return { w: 0, h: 0 };

  // Normalize unicode quotes/primes and fraction glyphs; "by" → "x".
  const s = String(str)
    .replace(/[“”″]/g, '"')
    .replace(/[‘’′]/g, "'")
    .replace(/½/g, ' 1/2')
    .replace(/¼/g, ' 1/4')
    .replace(/¾/g, ' 3/4')
    .replace(/⅓/g, ' 1/3')
    .replace(/⅔/g, ' 2/3')
    .replace(/⅛/g, ' 1/8')
    .replace(/\bby\b/gi, ' x ')
    .replace(/\s+/g, ' ')
    .trim();

  // A measurement: decimal, "whole frac" (13 1/2), bare fraction (3/4), .5
  const NUM = '(\\d+(?:\\.\\d+)?(?:\\s+\\d+/\\d+)?|\\.\\d+|\\d+/\\d+)';
  const toNum = tok => {
    let m = tok.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (m) return +m[1] + +m[2] / +m[3];
    m = tok.match(/^(\d+)\/(\d+)$/);
    if (m) return +m[1] / +m[2];
    return parseFloat(tok);
  };

  // Inches win: only treat as cm when there's a cm value AND no inch signal
  // (many strings are inches with "(30 x 40 cm)" appended).
  const hasInch = /(?:"|\bin\b|inch|inches)/i.test(s);
  const isCm = !hasInch && /\bcm\b/i.test(s);

  const grpRe = new RegExp(
    NUM + '\\s*["\']?\\s*[x×]\\s*' + NUM + '(?:\\s*["\']?\\s*[x×]\\s*' + NUM + ')?',
    'gi'
  );
  const groups = [...s.matchAll(grpRe)];

  let dims = null;
  if (groups.length) {
    let g;
    if (hasInch && /\bcm\b/i.test(s) && groups.length > 1) {
      // Both units present — take the group sitting next to an inch marker.
      g =
        groups.find(mm => /"|in/i.test(s.slice(mm.index, mm.index + mm[0].length + 5))) ||
        groups[0];
    } else {
      g = framed && groups.length > 1 ? groups[groups.length - 1] : groups[0];
    }
    dims = [g[1], g[2], g[3]].filter(Boolean).map(toNum);
  } else {
    // No "AxB": labeled (H/W in either order) or a single dimension.
    const lab = {};
    const set = (k, v) => {
      if (lab[k] == null) lab[k] = v;
    };
    const re1 = new RegExp('\\b([HWLD])\\s*[-:]?\\s*' + NUM, 'gi'); // H-17
    const re2 = new RegExp(NUM + '\\s*([HWLD])\\b', 'gi'); // 24 H
    for (const mm of s.matchAll(re1)) set(mm[1].toUpperCase(), toNum(mm[2]));
    for (const mm of s.matchAll(re2)) set(mm[2].toUpperCase(), toNum(mm[1]));
    if (lab.H != null && lab.W != null) {
      dims = [lab.H, lab.W];
    } else {
      const all = [...s.matchAll(new RegExp(NUM, 'g'))].map(mm => toNum(mm[1]));
      if (all.length) dims = all.slice(0, 3); // "34 1/4 round" → [34.25]
    }
  }
  if (!dims || !dims.length) return { w: 0, h: 0 };

  // 3 numbers → drop depth (the thinnest face: canvas/sculpture thickness).
  if (dims.length >= 3) {
    dims.splice(dims.indexOf(Math.min(...dims)), 1);
  }
  let [h, w] = dims.length === 1 ? [dims[0], dims[0]] : dims;
  if (isCm) {
    w /= 2.54;
    h /= 2.54;
  }
  return w > 0 && h > 0 ? { w, h } : { w: 0, h: 0 };
}

// Legacy `exact_dimensions` are inconsistently W×H vs H×W across the existing
// catalog (e.g. "Badminton" 16x20 is W×H; "Magpie"/"Avacados" 8x10 are H×W).
// The locked Height×Width convention is forward-only, so a fixed parse order
// is wrong for ~half the inventory and the piece renders rotated. Use the
// artwork image's OWN pixel orientation as ground truth and swap the parsed
// pair only when the image decisively disagrees.
//
// This is intentionally COARSE — it corrects ordering, not exact aspect.
// Photo-margin bleed (the listing photo includes background around the
// artwork rectangle) makes exact image-fit unreliable; tightening that is the
// separate autocrop work. The swap margin below is wide enough that margin
// bleed (a few % ratio perturbation) can never trigger a flip, while a true
// order inversion (the ratio's reciprocal) always clears it. Near-square
// pieces never swap (and wouldn't visibly differ if they did).
const ORIENT_SWAP_MARGIN = 0.15; // natural-log ratio units (~16% ratio gap)
function orientToImage(parsed, artAspect) {
  const { w, h } = parsed;
  if (!w || !h || !artAspect || artAspect <= 0) return parsed;
  const dimsRatio = w / h; // parsed width/height
  const errKeep = Math.abs(Math.log(dimsRatio) - Math.log(artAspect));
  const errSwap = Math.abs(Math.log(1 / dimsRatio) - Math.log(artAspect));
  return errKeep - errSwap > ORIENT_SWAP_MARGIN ? { w: h, h: w } : parsed;
}

const WallVisualizer = props => {
  const { artworkUrl, title, artist, year, medium, dimensions, isFramed, embedded } = props;
  const rootClass = embedded
    ? `${css.visualizerRoot} ${css.visualizerRootEmbedded}`
    : css.visualizerRoot;
  const sceneRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [artAspect, setArtAspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Parse real-world dimensions, then correct W×H/H×W ordering against the
  // image's actual orientation (legacy listings are inconsistent). artAspect
  // is null until the image loads; orientToImage no-ops until then, and
  // artAspect is already a getArtworkLayout dep so layout recomputes on load.
  const parsed = parseDimensions(dimensions, isFramed);
  const oriented = orientToImage(parsed, artAspect);
  const artWidthIn = oriented.w;
  const artHeightIn = oriented.h;

  // Load artwork image to get its natural aspect ratio
  useEffect(() => {
    if (!artworkUrl) {
      setLoading(false);
      setError(true);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setArtAspect(img.naturalWidth / img.naturalHeight);
      setLoading(false);
    };
    img.onerror = () => {
      setArtAspect(1);
      setLoading(false);
    };
    img.src = artworkUrl;
  }, [artworkUrl]);

  // The scene is a centered square "stage" — the largest square that fits
  // the available area. The room renders are square, so a square stage means
  // no cropping and the px/in calibration maps 1:1 to the image.
  const updateContainerSize = useCallback(() => {
    if (sceneRef.current) {
      const side = Math.min(sceneRef.current.clientWidth, sceneRef.current.clientHeight);
      setContainerSize({ width: side, height: side });
    }
  }, []);

  useEffect(() => {
    updateContainerSize();
    const el = sceneRef.current;
    let ro;
    // The scene lives inside a Modal/portal that has no laid-out size at
    // mount, so a one-shot measure reads 0 — which made getArtworkLayout
    // return artW:0 and the <img> fall back to width:100% (art rendered
    // full-scene regardless of real dimensions). Observe the element so we
    // re-measure the moment the modal lays it out, and on any later resize.
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => updateContainerSize());
      ro.observe(el);
    }
    window.addEventListener('resize', updateContainerSize);
    return () => {
      if (ro) {
        ro.disconnect();
      }
      window.removeEventListener('resize', updateContainerSize);
    };
  }, [updateContainerSize]);

  // Re-measure after loading completes
  useEffect(() => {
    if (!loading) {
      updateContainerSize();
    }
  }, [loading, updateContainerSize]);

  const metaParts = [artist, year, medium].filter(Boolean);

  // Calculate artwork pixel dimensions and vertical position for a given room
  const getArtworkLayout = useCallback(
    room => {
      const cw = containerSize.width;
      const ch = containerSize.height;
      if (!cw || !ch || !artAspect)
        return { artW: 0, artH: 0, topPct: room.centerYPct ?? room.anchorPct ?? 40 };

      // PPI: pixels per inch, scaled to the current container width. Every
      // room defines a calibrated pxPerInch, so there is no fallback path.
      const ppi = (cw / ROOM_NATIVE_WIDTH) * room.pxPerInch;

      let artW;
      if (artWidthIn > 0) {
        artW = Math.round(artWidthIn * ppi);
        // Cap at 60% of scene width
        artW = Math.min(artW, Math.round(cw * 0.6));
      } else {
        // Fallback: use room's default width percentage
        artW = Math.round(cw * (room.wall.width / 100));
      }

      const artH = artHeightIn > 0 ? Math.round(artHeightIn * ppi) : Math.round(artW / artAspect);

      // Vertical placement: most rooms bottom-anchor the art just above the
      // furniture (anchorPct = the bottom edge as a % of the scene). A room
      // may instead request true vertical CENTERING on the wall via
      // centerYPct (the art's center sits at that % of the scene) — used by
      // the gallery room. Rooms without centerYPct are unchanged.
      const artTopPx =
        room.centerYPct != null
          ? (room.centerYPct / 100) * ch - artH / 2
          : ((room.anchorPct ?? 45) / 100) * ch - artH;
      const topPct = (artTopPx / ch) * 100;

      return { artW, artH, topPct };
    },
    [containerSize, artAspect, artWidthIn, artHeightIn]
  );

  if (error) {
    return (
      <div className={rootClass}>
        <div className={css.sceneWrap} ref={sceneRef}>
          <div className={css.errorState}>
            <h2>Artwork not found</h2>
            <p>No artwork image available for this listing.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div className={css.sceneWrap} ref={sceneRef}>
        {loading || !containerSize.width ? (
          <div className={css.loadingOverlay}>
            <span className={css.loaderText}>Preparing the room&hellip;</span>
          </div>
        ) : (
          <div
            className={css.stage}
            style={{
              width: containerSize.width || '100%',
              height: containerSize.height || '100%',
            }}
          >
            {ROOMS.map((room, i) => {
              const layout = getArtworkLayout(room);
              const roomClasses = [css.room];
              if (i === activeIndex) {
                roomClasses.push(css.roomActive);
              }
              return (
                <div key={i} className={roomClasses.join(' ')}>
                  <img className={css.roomBg} src={room.src} alt={room.label} />
                  <div
                    className={css.artworkMount}
                    style={{
                      left: room.wall.left + '%',
                      top: layout.topPct + '%',
                    }}
                  >
                    <div className={css.artworkFrame}>
                      <img
                        className={css.artworkImg}
                        src={artworkUrl}
                        alt={title || 'Artwork'}
                        style={{
                          width: layout.artW > 0 ? layout.artW + 'px' : '100%',
                          height: layout.artH > 0 ? layout.artH + 'px' : 'auto',
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className={[css.infoPanel, css.infoPanelVisible].join(' ')}>
              <div className={css.infoTitle}>{title || 'Untitled'}</div>
              {metaParts.length > 0 ? (
                <div className={css.infoMeta}>{metaParts.join('  \u00b7  ')}</div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className={css.roomSwitcher}>
        <span className={css.switcherLabel}>View in room</span>
        {ROOMS.map((room, i) => {
          const thumbClasses = [css.roomThumb];
          if (i === activeIndex) {
            thumbClasses.push(css.roomThumbActive);
          }
          return (
            <img
              key={i}
              className={thumbClasses.join(' ')}
              src={room.src}
              alt={room.label}
              title={room.label}
              onClick={() => setActiveIndex(i)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default WallVisualizer;
